import {useEffect, useRef, useState, useCallback} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useDialog} from '@/hooks/useDialog'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {ErrorText} from '@/components/shared/ErrorText'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {useAppConfig, labelOf, type LocalizedTypeValue} from '@/hooks/use-app-config'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {
    PartyRelationshipSchemaControllerListEndpoint,
    PartyRelationshipSchemaControllerViolationsEndpoint,
} from '@/types/endpoint-contracts'
import {CONCEPT_ICON} from '@/lib/concepts'
import {formatTimestamp} from '@/lib/date-utils'
import type {PartyRelationshipSchemaEntry} from '@/types/party'
import {PartyRelationshipSchemaResponseFields, PartyRelationshipSchemaViolationResponseFields} from '@/types/party'
import {useRelationshipSchemaPermissions} from '@/features/relationship-schema/hooks/useRelationshipSchemaPermissions'
import {useRelationshipSchemaMutations} from '@/features/relationship-schema/hooks/useRelationshipSchemaMutations'
import {useRelationshipSchemaTab, RelationshipSchemaTab} from '@/features/relationship-schema/hooks/useRelationshipSchemaTab'
import {useCreateEntrySeed} from '@/features/relationship-schema/hooks/useCreateEntrySeed'
import {useNavigate} from 'react-router-dom'
import {routes} from '@/lib/routes'

/* eslint-disable i18next/no-literal-string */
const ErrorCode = {PS0001: 'PS0001'} as const
const ViolationType = {NO_SCHEMA_ENTRY: 'NO_SCHEMA_ENTRY'} as const
const INFINITY = '\u221E'
const ENTRIES_NAMESPACE = 'entries'
const VIOLATIONS_NAMESPACE = 'violations'
/* eslint-enable i18next/no-literal-string */

import {EnforcementMode} from '@/types/relationship-schema'

import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {Pagination} from '@/components/ui/pagination'
import {ErrorBanner} from '@/components/ui/error-banner'
import {SortableHeader} from '@/components/ui/sortable-header'
import {Skeleton} from '@/components/ui/skeleton'
import {AlertTriangle, Eye, EyeOff, Loader2, Network, Play, Plus} from 'lucide-react'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {LargeDialog} from '@/components/shared/LargeDialog'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {CompactButton} from '@/components/shared/CompactButton'
import {EnumSelectItems} from '@/components/shared/EnumSelectItems'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------


interface FormData {
    fromPartyType: string
    relationshipType: string
    toPartyType: string
    maxFromCount: number
    maxToCount: number
    fromSideDisplay: boolean
    toSideDisplay: boolean
    fromSideTree: boolean
    toSideTree: boolean
    fromSideLabel: string
    toSideLabel: string
    iconFrom: string
    iconTo: string
    aliasNameFrom: string
    aliasNameTo: string
    sortOrder: number
    active: boolean
}

const EMPTY_FORM: FormData = {
    fromPartyType: '',
    relationshipType: '',
    toPartyType: '',
    maxFromCount: 0,
    maxToCount: 0,
    fromSideDisplay: false,
    toSideDisplay: false,
    fromSideTree: false,
    toSideTree: false,
    fromSideLabel: '',
    toSideLabel: '',
    iconFrom: '',
    iconTo: '',
    aliasNameFrom: '',
    aliasNameTo: '',
    sortOrder: 0,
    active: true,
}

// Mirror of backend regex in PartyRelationshipSchemaService.ALIAS_NAME_PATTERN.
const ALIAS_NAME_REGEX = /^[a-z][a-zA-Z0-9]*$/

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function RelationshipSchema() {
    const i18n = useI18n()
    usePageTitle(i18n.partyRelationshipSchema.pageTitle)
    const services = useServices()
    const navigate = useNavigate()
    const {partyTypes, partyRelationshipTypes} = useAppConfig()
    const {canView, canManage, canCreate, canRunValidation} = useRelationshipSchemaPermissions()
    const {activeTab, setActiveTab} = useRelationshipSchemaTab()

    const openCreateRef = useRef<((seed?: {fromPartyType: string; relationshipType: string; toPartyType: string}) => void) | null>(null)
    const registerOpenCreate = useCallback(
        (fn: ((seed?: {fromPartyType: string; relationshipType: string; toPartyType: string}) => void) | null) => {
            openCreateRef.current = fn
        },
        [],
    )

    const {data: entriesCountData} = useQuery({
        queryKey: ['relationship-schema-count'],
        queryFn: () => services.partyRelationshipSchema.list({size: 1}),
        enabled: canView,
    })
    const {data: violationsCountData} = useQuery({
        queryKey: ['relationship-schema-violations-count'],
        queryFn: () => services.partyRelationshipSchema.violations({size: 1}),
        enabled: canView,
    })
    const entriesCount = entriesCountData?.totalElements
    const violationsCount = violationsCountData?.totalElements

    /* eslint-disable i18next/no-literal-string */
    const {data: validationJob} = useQuery({
        queryKey: ['jobs', 'by-key', 'party-relationship-schema-validation'],
        queryFn: () => services.jobs.byKey('party-relationship-schema-validation'),
        enabled: canView,
    })
    /* eslint-enable i18next/no-literal-string */

    const {runValidation} = useRelationshipSchemaMutations()

    if (!canView) {
        return (
            <InlineEmptyText>{i18n.partyRelationshipSchema.noPermission}</InlineEmptyText>
        )
    }

    const tabs: TabDefinition[] = [
        {
            key: RelationshipSchemaTab.ENTRIES,
            label: String(i18n.partyRelationshipSchema.tabs.schemaEntries),
            count: entriesCount && entriesCount > 0 ? entriesCount : undefined,
        },
        {
            key: RelationshipSchemaTab.VIOLATIONS,
            label: String(i18n.partyRelationshipSchema.tabs.violations),
            count: violationsCount && violationsCount > 0 ? violationsCount : undefined,
        },
    ]

    let actions: React.ReactNode = undefined
    if (canCreate && activeTab === RelationshipSchemaTab.ENTRIES) {
        actions = (
            <Button
                onClick={() => openCreateRef.current?.()}
                size="sm"
                data-testid="btn-create-schema"
            >
                <Icon icon={Plus} size="sm" className="mr-1"/>
                {i18n.partyRelationshipSchema.actions.create}
            </Button>
        )
    } else if (canRunValidation && activeTab === RelationshipSchemaTab.VIOLATIONS && validationJob) {
        actions = (
            <Button
                variant="outline"
                size="sm"
                data-testid="run-validation-button"
                disabled={runValidation.isPending}
                onClick={() => runValidation.mutate(validationJob.id)}
            >
                <Icon
                    icon={runValidation.isPending ? Loader2 : Play}
                    size="xs"
                    spin={runValidation.isPending}
                    className="mr-1.5"
                />
                {i18n.partyRelationshipSchema.violations.runValidation}
            </Button>
        )
    }

    return (
        <PageLayout
            data-testid="relationship-schema-page"
            variant="tabbed-list"
            title={i18n.partyRelationshipSchema.title}
            description={i18n.partyRelationshipSchema.subtitle}
            icon={CONCEPT_ICON.relationshipSchema}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(key: string) => setActiveTab(key as RelationshipSchemaTab)}
            actions={actions}
        >
            {activeTab === RelationshipSchemaTab.ENTRIES && (
                <SchemaEntriesTab
                    canManage={canManage}
                    partyTypes={partyTypes}
                    partyRelationshipTypes={partyRelationshipTypes}
                    registerOpenCreate={registerOpenCreate}
                />
            )}
            {activeTab === RelationshipSchemaTab.VIOLATIONS && (
                <ViolationsTab
                    partyTypes={partyTypes}
                    partyRelationshipTypes={partyRelationshipTypes}
                    canManage={canManage}
                    onSeedCreateEntry={(seed) => {
                        navigate(routes.schema.relationshipSchema.seedFromViolation(seed))
                    }}
                />
            )}
        </PageLayout>
    )
}

// ---------------------------------------------------------------------------
// Schema Entries Tab
// ---------------------------------------------------------------------------

interface SchemaEntriesTabProps {
    canManage: boolean
    partyTypes: LocalizedTypeValue[]
    partyRelationshipTypes: LocalizedTypeValue[]
    registerOpenCreate: (fn: ((seed?: {fromPartyType: string; relationshipType: string; toPartyType: string}) => void) | null) => void
}

function SchemaEntriesTab({canManage, partyTypes, partyRelationshipTypes, registerOpenCreate}: SchemaEntriesTabProps) {
    const i18n = useI18n()
    const services = useServices()

    const dialog = useDialog()
    const [editEntry, setEditEntry] = useState<PartyRelationshipSchemaEntry | null>(null)
    const [viewOnly, setViewOnly] = useState(false)
    const [deactivateEntry, setDeactivateEntry] = useState<PartyRelationshipSchemaEntry | null>(null)
    const [formData, setFormData] = useState<FormData>(EMPTY_FORM)
    const [formError, setFormError] = useState('')
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const {seed, seedOpen, clearSeed} = useCreateEntrySeed()

    const {
        page, pageSize, sortBy, sortDir, setSort, setPage, setPageSize,
        pageSizeOptions, queryString,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: PartyRelationshipSchemaControllerListEndpoint,
        sortable: true,
        defaultSortBy: PartyRelationshipSchemaControllerListEndpoint.sort.SORT_ORDER,
        defaultSortDir: SortDir.ASC,
        defaultPageSize: 25,
        namespace: ENTRIES_NAMESPACE,
    })

    const {data, isLoading, error} = useQuery({
        queryKey: ['relationship-schema', queryString],
        queryFn: () => services.partyRelationshipSchema.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const entries = data?.content ?? []
    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0

    const {createEntry, updateEntry, deactivateEntry: deactivateMutation} = useRelationshipSchemaMutations({
        onCreateSuccess: () => closeDialog(),
        onCreateError: (err) => {
            const apiErr = err as {response?: {data?: {errors?: {code?: string}[]}}}
            const code = apiErr?.response?.data?.errors?.[0]?.code
            if (code === ErrorCode.PS0001) {
                setFormError(i18n.partyRelationshipSchema.messages.duplicateTriple)
            }
        },
        onUpdateSuccess: () => closeDialog(),
        onDeactivateSuccess: () => setDeactivateEntry(null),
    })

    const openCreate = useCallback(
        (presets?: {fromPartyType: string; relationshipType: string; toPartyType: string}) => {
            setEditEntry(null)
            setViewOnly(false)
            setFormData(presets
                ? {
                    ...EMPTY_FORM,
                    fromPartyType: presets.fromPartyType,
                    relationshipType: presets.relationshipType,
                    toPartyType: presets.toPartyType,
                }
                : EMPTY_FORM,
            )
            setFormError('')
            setFieldErrors({})
            dialog.open()
        },
        [dialog],
    )

    useEffect(() => {
        registerOpenCreate(openCreate)
        return () => registerOpenCreate(null)
    }, [registerOpenCreate, openCreate])

    useEffect(() => {
        if (seedOpen && !dialog.isOpen && !editEntry) {
            openCreate(seed ?? undefined)
        }
    }, [seedOpen, seed, dialog.isOpen, editEntry, openCreate])

    function openEdit(entry: PartyRelationshipSchemaEntry) {
        setEditEntry(entry)
        setViewOnly(!canManage)
        setFormData({
            fromPartyType: entry.fromPartyType,
            relationshipType: entry.relationshipType,
            toPartyType: entry.toPartyType,
            maxFromCount: entry.maxFromCount,
            maxToCount: entry.maxToCount,
            fromSideDisplay: entry.fromSideDisplay,
            toSideDisplay: entry.toSideDisplay,
            fromSideTree: entry.fromSideTree,
            toSideTree: entry.toSideTree,
            fromSideLabel: entry.fromSideLabel ?? '',
            toSideLabel: entry.toSideLabel ?? '',
            iconFrom: entry.iconFrom ?? '',
            iconTo: entry.iconTo ?? '',
            aliasNameFrom: entry.aliasNameFrom ?? '',
            aliasNameTo: entry.aliasNameTo ?? '',
            sortOrder: entry.sortOrder,
            active: entry.active,
        })
        setFormError('')
        setFieldErrors({})
        dialog.open()
    }

    function closeDialog() {
        dialog.close()
        setEditEntry(null)
        setViewOnly(false)
        clearSeed()
    }

    function validateAndSubmit() {
        const errors: Record<string, string> = {}
        if (!editEntry) {
            if (!formData.fromPartyType) errors.fromPartyType = i18n.partyRelationshipSchema.form.required
            if (!formData.relationshipType) errors.relationshipType = i18n.partyRelationshipSchema.form.required
            if (!formData.toPartyType) errors.toPartyType = i18n.partyRelationshipSchema.form.required
        }
        if (formData.maxFromCount < 0) errors.maxFromCount = i18n.partyRelationshipSchema.form.minZero
        if (formData.maxToCount < 0) errors.maxToCount = i18n.partyRelationshipSchema.form.minZero
        if (formData.sortOrder < 0) errors.sortOrder = i18n.partyRelationshipSchema.form.minZero
        if (formData.fromSideDisplay && !formData.fromSideLabel.trim()) {
            errors.fromSideLabel = i18n.partyRelationshipSchema.form.labelRequired
        }
        if (formData.toSideDisplay && !formData.toSideLabel.trim()) {
            errors.toSideLabel = i18n.partyRelationshipSchema.form.labelRequired
        }
        if (formData.aliasNameFrom.trim() && !ALIAS_NAME_REGEX.test(formData.aliasNameFrom.trim())) {
            errors.aliasNameFrom = i18n.partyRelationshipSchema.form.aliasNameInvalid
        }
        if (formData.aliasNameTo.trim() && !ALIAS_NAME_REGEX.test(formData.aliasNameTo.trim())) {
            errors.aliasNameTo = i18n.partyRelationshipSchema.form.aliasNameInvalid
        }

        setFieldErrors(errors)
        if (Object.keys(errors).length > 0) return

        if (editEntry) {
            updateEntry.mutation.mutate({
                id: editEntry.schemaEntryId,
                fromPartyType: editEntry.fromPartyType,
                relationshipType: editEntry.relationshipType,
                toPartyType: editEntry.toPartyType,
                maxFromCount: formData.maxFromCount,
                maxToCount: formData.maxToCount,
                fromSideDisplay: formData.fromSideDisplay,
                toSideDisplay: formData.toSideDisplay,
                fromSideTree: formData.fromSideTree,
                toSideTree: formData.toSideTree,
                fromSideLabel: formData.fromSideLabel || null,
                toSideLabel: formData.toSideLabel || null,
                iconFrom: formData.iconFrom || null,
                iconTo: formData.iconTo || null,
                aliasNameFrom: formData.aliasNameFrom || null,
                aliasNameTo: formData.aliasNameTo || null,
                sortOrder: formData.sortOrder,
                active: formData.active,
                version: editEntry.version,
            })
        } else {
            createEntry.mutate({
                fromPartyType: formData.fromPartyType,
                relationshipType: formData.relationshipType,
                toPartyType: formData.toPartyType,
                maxFromCount: formData.maxFromCount,
                maxToCount: formData.maxToCount,
                fromSideDisplay: formData.fromSideDisplay,
                toSideDisplay: formData.toSideDisplay,
                fromSideTree: formData.fromSideTree,
                toSideTree: formData.toSideTree,
                fromSideLabel: formData.fromSideLabel || null,
                toSideLabel: formData.toSideLabel || null,
                iconFrom: formData.iconFrom || null,
                iconTo: formData.iconTo || null,
                aliasNameFrom: formData.aliasNameFrom || null,
                aliasNameTo: formData.aliasNameTo || null,
                sortOrder: formData.sortOrder,
            })
        }
    }

    const isPending = createEntry.isPending || updateEntry.mutation.isPending

    return (
        <>
            <TabPanelContent
                filters={
                    <UnifiedSearchBar
                        contract={PartyRelationshipSchemaControllerListEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                    />
                }
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page} pageSize={pageSize}
                        totalItems={totalItems} totalPages={totalPages}
                        onPageChange={setPage} onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                    {error && <ErrorBanner error={error as Error}/>}

                    {isLoading ? (
                        <Stack gap="sm">
                            {Array.from({length: 5}).map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full"/>
                            ))}
                        </Stack>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-from-type">
                                            <SortableHeader
                                                label={i18n.partyRelationshipSchema.columns.fromType}
                                                sortKey={PartyRelationshipSchemaResponseFields.fromPartyType} currentSortBy={sortBy}
                                                currentSortDir={sortDir} onSort={setSort}/>
                                        </TableHead>
                                        <TableHead data-testid="col-relationship-type">
                                            <SortableHeader
                                                label={i18n.partyRelationshipSchema.columns.relationshipType}
                                                sortKey={PartyRelationshipSchemaResponseFields.relationshipType} currentSortBy={sortBy}
                                                currentSortDir={sortDir} onSort={setSort}/>
                                        </TableHead>
                                        <TableHead data-testid="col-to-type">
                                            <SortableHeader
                                                label={i18n.partyRelationshipSchema.columns.toType}
                                                sortKey={PartyRelationshipSchemaResponseFields.toPartyType} currentSortBy={sortBy}
                                                currentSortDir={sortDir} onSort={setSort}/>
                                        </TableHead>
                                        <TableHead data-testid="col-cardinality">{i18n.partyRelationshipSchema.columns.cardinality}</TableHead>
                                        <TableHead data-testid="col-display">{i18n.partyRelationshipSchema.columns.display}</TableHead>
                                        <TableHead data-testid="col-status">
                                            <SortableHeader
                                                label={i18n.partyRelationshipSchema.columns.status}
                                                sortKey={PartyRelationshipSchemaResponseFields.active} currentSortBy={sortBy}
                                                currentSortDir={sortDir} onSort={setSort}/>
                                        </TableHead>
                                        {canManage && <TableHead data-testid="col-actions"/>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {entries.map(entry => (
                                        <TableRow
                                            key={entry.schemaEntryId}
                                            className="cursor-pointer"
                                            onClick={() => openEdit(entry)}
                                        >
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {labelOf(partyTypes, entry.fromPartyType)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {labelOf(partyRelationshipTypes, entry.relationshipType)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {labelOf(partyTypes, entry.toPartyType)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {formatCardinality(entry.maxFromCount)} / {formatCardinality(entry.maxToCount)}
                                            </TableCell>
                                            <TableCell>
                                                <InlineRow gap="xs">
                                                    {entry.fromSideDisplay
                                                        ? <Icon icon={Eye}/>
                                                        : <Icon icon={EyeOff} muted/>}
                                                    {entry.toSideDisplay
                                                        ? <Icon icon={Eye}/>
                                                        : <Icon icon={EyeOff} muted/>}
                                                    {(entry.fromSideTree || entry.toSideTree) && (
                                                        <Icon icon={Network} className="text-primary"/>
                                                    )}
                                                </InlineRow>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={entry.active ? BadgeVariant.SUCCESS : BadgeVariant.WARNING}>
                                                    {entry.active ? i18n.common.active : i18n.common.inactive}
                                                </Badge>
                                            </TableCell>
                                            {canManage && (
                                                <TableCell className="text-right">
                                                    {entry.active && (
                                                        <Button variant="ghost" size="sm" data-testid="btn-deactivate" onClick={e => {
                                                            e.stopPropagation()
                                                            setDeactivateEntry(entry)
                                                        }}>
                                                            {i18n.partyRelationshipSchema.actions.deactivate}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                    {entries.length === 0 && !isLoading && (
                                        <TableRow>
                                            <TableCell colSpan={canManage ? 7 : 6}
                                                       className="text-center py-8">
                                                <MutedText>{i18n.common.noResults}</MutedText>
                                                {canManage && (
                                                        <Button variant="outline" size="sm" onClick={() => openCreate()} data-testid="btn-create-schema-empty" className="mt-2">
                                                            <Icon icon={Plus} size="sm" className="mr-1"/>
                                                            {i18n.partyRelationshipSchema.actions.create}
                                                        </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>

                        </>
                    )}
            </TabPanelContent>

            {/* Create/Edit Dialog */}
            <Dialog open={dialog.isOpen} onOpenChange={v => {
                if (!v) closeDialog()
            }}>
                <LargeDialog>
                    <DialogHeader>
                        <DialogTitle data-testid="dialog-title">
                            {viewOnly
                                ? i18n.partyRelationshipSchema.form.detailTitle
                                : editEntry
                                    ? i18n.partyRelationshipSchema.form.editTitle
                                    : i18n.partyRelationshipSchema.form.createTitle}
                        </DialogTitle>
                        {!viewOnly && (
                            <DialogDescription>
                                {editEntry
                                    ? i18n.partyRelationshipSchema.form.editDescription
                                    : i18n.partyRelationshipSchema.form.createDescription}
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    {formError && (
                        <InfoBanner variant="destructive">{formError}</InfoBanner>
                    )}

                    <Stack gap="md" className="py-2">
                        {/* Triple fields */}
                        <FormGrid>
                            <Label>{i18n.partyRelationshipSchema.form.fromPartyType}</Label>
                            <Select value={formData.fromPartyType}
                                    onValueChange={v => setFormData({...formData, fromPartyType: v})}
                                    disabled={!!editEntry || viewOnly}>
                                <SelectTrigger data-testid="select-from-party-type">
                                    <SelectValue
                                        placeholder={i18n.partyRelationshipSchema.form.fromPartyType}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {partyTypes.map(t => (
                                        <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {fieldErrors.fromPartyType && (
                                <ErrorText>{fieldErrors.fromPartyType}</ErrorText>
                            )}
                        </FormGrid>

                        <FormGrid>
                            <Label>{i18n.partyRelationshipSchema.form.relationshipType}</Label>
                            <Select value={formData.relationshipType}
                                    onValueChange={v => setFormData({...formData, relationshipType: v})}
                                    disabled={!!editEntry || viewOnly}>
                                <SelectTrigger data-testid="select-relationship-type">
                                    <SelectValue
                                        placeholder={i18n.partyRelationshipSchema.form.relationshipType}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {partyRelationshipTypes.map(t => (
                                        <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {fieldErrors.relationshipType && (
                                <ErrorText>{fieldErrors.relationshipType}</ErrorText>
                            )}
                        </FormGrid>

                        <FormGrid>
                            <Label>{i18n.partyRelationshipSchema.form.toPartyType}</Label>
                            <Select value={formData.toPartyType}
                                    onValueChange={v => setFormData({...formData, toPartyType: v})}
                                    disabled={!!editEntry || viewOnly}>
                                <SelectTrigger data-testid="select-to-party-type">
                                    <SelectValue
                                        placeholder={i18n.partyRelationshipSchema.form.toPartyType}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {partyTypes.map(t => (
                                        <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {fieldErrors.toPartyType && (
                                <ErrorText>{fieldErrors.toPartyType}</ErrorText>
                            )}
                        </FormGrid>

                        {/* Cardinality */}
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.maxFromCount}</Label>
                                <Input type="number" min={0} value={formData.maxFromCount}
                                       disabled={viewOnly}
                                       onChange={e => setFormData({
                                           ...formData,
                                           maxFromCount: parseInt(e.target.value) || 0,
                                       })}/>
                                <MutedText size="xs">
                                    {i18n.partyRelationshipSchema.form.maxFromCountHint}
                                </MutedText>
                                {fieldErrors.maxFromCount && (
                                    <ErrorText>{fieldErrors.maxFromCount}</ErrorText>
                                )}
                            </FormGrid>
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.maxToCount}</Label>
                                <Input type="number" min={0} value={formData.maxToCount}
                                       disabled={viewOnly}
                                       onChange={e => setFormData({
                                           ...formData,
                                           maxToCount: parseInt(e.target.value) || 0,
                                       })}/>
                                <MutedText size="xs">
                                    {i18n.partyRelationshipSchema.form.maxToCountHint}
                                </MutedText>
                                {fieldErrors.maxToCount && (
                                    <ErrorText>{fieldErrors.maxToCount}</ErrorText>
                                )}
                            </FormGrid>
                        </DetailGrid>

                        {/* Display & tree toggles */}
                        <DetailGrid columns={2} responsive={false}>
                            <Stack gap="sm">
                                <InlineRow>
                                    <Switch checked={formData.fromSideDisplay}
                                            disabled={viewOnly}
                                            data-testid="switch-from-side-display"
                                            onCheckedChange={v => setFormData({
                                                ...formData,
                                                fromSideDisplay: v,
                                                fromSideLabel: v ? formData.fromSideLabel : '',
                                            })}/>
                                    <Label>{i18n.partyRelationshipSchema.form.fromSideDisplay}</Label>
                                </InlineRow>
                                <InlineRow>
                                    <Switch checked={formData.fromSideTree}
                                            disabled={viewOnly}
                                            data-testid="switch-from-side-tree"
                                            onCheckedChange={v => setFormData({
                                                ...formData,
                                                fromSideTree: v,
                                            })}/>
                                    <Label>{i18n.partyRelationshipSchema.form.fromSideTree}</Label>
                                </InlineRow>
                            </Stack>
                            <Stack gap="sm">
                                <InlineRow>
                                    <Switch checked={formData.toSideDisplay}
                                            disabled={viewOnly}
                                            data-testid="switch-to-side-display"
                                            onCheckedChange={v => setFormData({
                                                ...formData,
                                                toSideDisplay: v,
                                                toSideLabel: v ? formData.toSideLabel : '',
                                            })}/>
                                    <Label>{i18n.partyRelationshipSchema.form.toSideDisplay}</Label>
                                </InlineRow>
                                <InlineRow>
                                    <Switch checked={formData.toSideTree}
                                            disabled={viewOnly}
                                            data-testid="switch-to-side-tree"
                                            onCheckedChange={v => setFormData({
                                                ...formData,
                                                toSideTree: v,
                                            })}/>
                                    <Label>{i18n.partyRelationshipSchema.form.toSideTree}</Label>
                                </InlineRow>
                            </Stack>
                        </DetailGrid>

                        {/* Labels (conditional) */}
                        {formData.fromSideDisplay && (
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.fromSideLabel}</Label>
                                <Input value={formData.fromSideLabel} disabled={viewOnly}
                                       data-testid="input-from-side-label"
                                       onChange={e => setFormData({
                                           ...formData,
                                           fromSideLabel: e.target.value,
                                       })}/>
                                {fieldErrors.fromSideLabel && (
                                    <ErrorText>{fieldErrors.fromSideLabel}</ErrorText>
                                )}
                            </FormGrid>
                        )}
                        {formData.toSideDisplay && (
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.toSideLabel}</Label>
                                <Input value={formData.toSideLabel} disabled={viewOnly}
                                       data-testid="input-to-side-label"
                                       onChange={e => setFormData({
                                           ...formData,
                                           toSideLabel: e.target.value,
                                       })}/>
                                {fieldErrors.toSideLabel && (
                                    <ErrorText>{fieldErrors.toSideLabel}</ErrorText>
                                )}
                            </FormGrid>
                        )}

                        {/* Icons */}
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.iconFrom}</Label>
                                <Input value={formData.iconFrom} disabled={viewOnly}
                                       placeholder={i18n.partyRelationshipSchema.form.searchIcon}
                                       onChange={e => setFormData({
                                           ...formData, iconFrom: e.target.value,
                                       })}/>
                            </FormGrid>
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.iconTo}</Label>
                                <Input value={formData.iconTo} disabled={viewOnly}
                                       placeholder={i18n.partyRelationshipSchema.form.searchIcon}
                                       onChange={e => setFormData({
                                           ...formData, iconTo: e.target.value,
                                       })}/>
                            </FormGrid>
                        </DetailGrid>

                        {/* Expression-language aliases (501.07) */}
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.aliasNameFrom}</Label>
                                <Input value={formData.aliasNameFrom} disabled={viewOnly}
                                       placeholder={i18n.partyRelationshipSchema.form.aliasNamePlaceholder}
                                       data-testid="input-alias-name-from"
                                       onChange={e => setFormData({
                                           ...formData, aliasNameFrom: e.target.value,
                                       })}/>
                                <MutedText size="xs">
                                    {i18n.partyRelationshipSchema.form.aliasNameHint}
                                </MutedText>
                                {fieldErrors.aliasNameFrom && (
                                    <ErrorText>{fieldErrors.aliasNameFrom}</ErrorText>
                                )}
                            </FormGrid>
                            <FormGrid>
                                <Label>{i18n.partyRelationshipSchema.form.aliasNameTo}</Label>
                                <Input value={formData.aliasNameTo} disabled={viewOnly}
                                       placeholder={i18n.partyRelationshipSchema.form.aliasNamePlaceholder}
                                       data-testid="input-alias-name-to"
                                       onChange={e => setFormData({
                                           ...formData, aliasNameTo: e.target.value,
                                       })}/>
                                <MutedText size="xs">
                                    {i18n.partyRelationshipSchema.form.aliasNameHint}
                                </MutedText>
                                {fieldErrors.aliasNameTo && (
                                    <ErrorText>{fieldErrors.aliasNameTo}</ErrorText>
                                )}
                            </FormGrid>
                        </DetailGrid>

                        {/* Sort order */}
                        <FormGrid>
                            <Label>{i18n.partyRelationshipSchema.form.sortOrder}</Label>
                            <Input type="number" min={0} value={formData.sortOrder}
                                   disabled={viewOnly}
                                   onChange={e => setFormData({
                                       ...formData,
                                       sortOrder: parseInt(e.target.value) || 0,
                                   })}/>
                            {fieldErrors.sortOrder && (
                                <ErrorText>{fieldErrors.sortOrder}</ErrorText>
                            )}
                        </FormGrid>

                        {/* Active (edit only) */}
                        {editEntry && (
                            <InlineRow>
                                <Switch checked={formData.active} disabled={viewOnly}
                                        data-testid="switch-edit-active"
                                        onCheckedChange={v => setFormData({
                                            ...formData, active: v,
                                        })}/>
                                <Label>{i18n.partyRelationshipSchema.form.active}</Label>
                            </InlineRow>
                        )}
                    </Stack>

                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog} data-testid="btn-dialog-cancel">
                            {i18n.common.cancel}
                        </Button>
                        {!viewOnly && (
                            <Button onClick={validateAndSubmit} disabled={isPending} data-testid="btn-dialog-save">
                                {isPending ? i18n.common.saving : i18n.partyRelationshipSchema.actions.save}
                            </Button>
                        )}
                    </DialogFooter>
                </LargeDialog>
            </Dialog>

            {/* Deactivate Dialog */}
            <Dialog open={!!deactivateEntry} onOpenChange={v => {
                if (!v) setDeactivateEntry(null)
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {i18n.partyRelationshipSchema.messages.deactivateTitle}
                        </DialogTitle>
                        <DialogDescription>
                            {deactivateEntry && i18n.partyRelationshipSchema.messages.deactivateDescription({
                                from: labelOf(partyTypes, deactivateEntry.fromPartyType),
                                rel: labelOf(partyRelationshipTypes, deactivateEntry.relationshipType),
                                to: labelOf(partyTypes, deactivateEntry.toPartyType),
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="deactivate"
                        onCancel={() => setDeactivateEntry(null)}
                        saving={deactivateMutation.isPending}
                        saveLabel={i18n.partyRelationshipSchema.actions.deactivate}
                        destructive
                        saveType="button"
                        onSave={() => {
                            if (deactivateEntry) {
                                deactivateMutation.mutate(deactivateEntry.schemaEntryId)
                            }
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

// ---------------------------------------------------------------------------
// Violations Tab
// ---------------------------------------------------------------------------

interface ViolationsTabProps {
    partyTypes: LocalizedTypeValue[]
    partyRelationshipTypes: LocalizedTypeValue[]
    canManage: boolean
    onSeedCreateEntry: (seed: {fromPartyType: string; relationshipType: string; toPartyType: string}) => void
}

function ViolationsTab({partyTypes, partyRelationshipTypes, canManage, onSeedCreateEntry}: ViolationsTabProps) {
    const i18n = useI18n()
    const services = useServices()

    const {
        page, pageSize, sortBy, sortDir, setSort, setPage, setPageSize,
        pageSizeOptions, queryString,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: PartyRelationshipSchemaControllerViolationsEndpoint,
        sortable: true,
        defaultSortBy: PartyRelationshipSchemaControllerViolationsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
        defaultPageSize: 25,
        namespace: VIOLATIONS_NAMESPACE,
    })

    const {data: modeData} = useQuery({
        queryKey: ['relationship-schema', 'enforcement-mode'],
        queryFn: () => services.partyRelationshipSchema.enforcementMode(),
    })

    const {data, isLoading, error} = useQuery({
        queryKey: ['relationship-schema', 'violations', queryString],
        queryFn: () => services.partyRelationshipSchema.violations(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const violations = data?.content ?? []
    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0

    const mode = (modeData as {mode?: string} | undefined)?.mode ?? EnforcementMode.OFF.code

    return (
        <TabPanelContent
            filters={
                <UnifiedSearchBar
                    contract={PartyRelationshipSchemaControllerViolationsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page} pageSize={pageSize}
                    totalItems={totalItems} totalPages={totalPages}
                    onPageChange={setPage} onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
                <EnforcementBanner mode={mode} canManage={canManage}/>

                {error && <ErrorBanner error={error as Error}/>}

                {isLoading ? (
                    <Stack gap="sm">
                        {Array.from({length: 5}).map((_, i) => (
                            <Skeleton key={i} className="h-12 w-full"/>
                        ))}
                    </Stack>
                ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="violation-col-type">{i18n.partyRelationshipSchema.violations.violationType}</TableHead>
                                    <TableHead data-testid="violation-col-from-type">{i18n.partyRelationshipSchema.columns.fromType}</TableHead>
                                    <TableHead data-testid="violation-col-relationship-type">{i18n.partyRelationshipSchema.columns.relationshipType}</TableHead>
                                    <TableHead data-testid="violation-col-to-type">{i18n.partyRelationshipSchema.columns.toType}</TableHead>
                                    <TableHead data-testid="violation-col-detail">{i18n.partyRelationshipSchema.violations.detail}</TableHead>
                                    <TableHead data-testid="violation-col-created-at">
                                        <SortableHeader
                                            label={i18n.partyRelationshipSchema.violations.createdAt}
                                            sortKey={PartyRelationshipSchemaViolationResponseFields.createdAt} currentSortBy={sortBy}
                                            currentSortDir={sortDir} onSort={setSort}/>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {violations.map(v => {
                                    const isNoSchemaEntry = v.violationType === ViolationType.NO_SCHEMA_ENTRY
                                    const seedClickable = canManage && isNoSchemaEntry
                                    return (
                                    <TableRow
                                        key={v.violationId}
                                        data-testid={isNoSchemaEntry ? `violation-row-no-schema-${v.violationId}` : `violation-row-${v.violationId}`}
                                        data-readonly={!seedClickable || undefined}
                                        data-violation-type={v.violationType}
                                        onClick={seedClickable ? () => onSeedCreateEntry({
                                            fromPartyType: v.fromPartyType,
                                            relationshipType: v.relationshipType,
                                            toPartyType: v.toPartyType,
                                        }) : undefined}
                                    >
                                        <TableCell>
                                            <Badge variant={isNoSchemaEntry ? 'warning' : 'destructive'} data-testid={isNoSchemaEntry ? 'violation-badge-no-schema' : 'violation-badge-cardinality'}>
                                                {isNoSchemaEntry
                                                    ? i18n.partyRelationshipSchema.violations.noSchemaEntry
                                                    : i18n.partyRelationshipSchema.violations.cardinalityExceeded}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {labelOf(partyTypes, v.fromPartyType)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {labelOf(partyRelationshipTypes, v.relationshipType)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {labelOf(partyTypes, v.toPartyType)}
                                            </Badge>
                                        </TableCell>
                                        <TruncatedCell maxWidth="320px">
                                            {v.detail ?? '—'}
                                        </TruncatedCell>
                                        <TableCell>{formatTimestamp(v.createdAt)}</TableCell>
                                    </TableRow>
                                    )
                                })}
                                {violations.length === 0 && !isLoading && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8">
                                            <Icon icon={AlertTriangle} size="xl" muted className="mx-auto mb-2"/>
                                            <MutedText>{i18n.partyRelationshipSchema.violations.empty}</MutedText>
                                            <MutedText size="xs" className="mt-1">
                                                {i18n.partyRelationshipSchema.violations.emptyDescription}
                                            </MutedText>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                )}
        </TabPanelContent>
    )
}

// ---------------------------------------------------------------------------
// Enforcement Banner
// ---------------------------------------------------------------------------

function EnforcementBanner({mode, canManage}: {mode: string, canManage: boolean}) {
    const i18n = useI18n()
    const dialog = useDialog()
    const [selectedMode, setSelectedMode] = useState(mode)

    useEffect(() => {
        if (!dialog.isOpen) {
            setSelectedMode(mode)
        }
    }, [mode, dialog.isOpen])

    const {updateEnforcementMode} = useRelationshipSchemaMutations({
        onUpdateEnforcementModeSuccess: () => dialog.close(),
    })

    const resolved = EnforcementMode.fromCode(mode)
    const label = resolved ? resolved.label(i18n) : mode
    const variant = resolved ? resolved.variant : BadgeVariant.OUTLINE

    const selectedModeObj = EnforcementMode.fromCode(selectedMode)

    return (
        <>
            <InlineRow justify="between" className="mt-3" data-testid="enforcement-banner">
                <Badge variant={variant} data-testid="enforcement-mode-text">{label}</Badge>
                {canManage && (
                    <CompactButton
                        variant="outline"
                        size="sm"
                        data-testid="enforcement-change-mode-button"
                        onClick={() => { setSelectedMode(mode); dialog.open() }}
                    >
                        {i18n.partyRelationshipSchema.enforcement.changeMode}
                    </CompactButton>
                )}
            </InlineRow>

            <Dialog open={dialog.isOpen} onOpenChange={dialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.partyRelationshipSchema.enforcement.changeModeTitle}</DialogTitle>
                        <DialogDescription>{i18n.partyRelationshipSchema.enforcement.changeModeDescription}</DialogDescription>
                    </DialogHeader>

                    <InfoBanner variant="info" icon={AlertTriangle}>
                        {i18n.partyRelationshipSchema.enforcement.changeModeWarning}
                    </InfoBanner>

                    <Stack gap="sm">
                        <Label>{i18n.partyRelationshipSchema.enforcement.newMode}</Label>
                        <Select value={selectedMode} onValueChange={setSelectedMode}>
                            <SelectTrigger data-testid="enforcement-mode-select">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                <EnumSelectItems
                                    enumClass={EnforcementMode}
                                    labelFn={(v, i) => v.shortLabel(i)}
                                    testIdPrefix="enforcement-mode-option"
                                />
                            </SelectContent>
                        </Select>
                        {selectedModeObj && (
                            <MutedText size="sm" data-testid={`enforcement-mode-description-${selectedModeObj.code}`}>
                                {selectedModeObj.description(i18n)}
                            </MutedText>
                        )}
                    </Stack>

                    <SaveCancelFooter
                        testIdPrefix="enforcement-mode"
                        onCancel={dialog.close}
                        saving={updateEnforcementMode.isPending}
                        disabled={selectedMode === mode}
                        saveLabel={i18n.partyRelationshipSchema.enforcement.confirm}
                        destructive={selectedMode === EnforcementMode.OFF.code}
                        saveType="button"
                        onSave={() => updateEnforcementMode.mutate(selectedMode)}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCardinality(count: number): string {
    return count === 0 ? INFINITY : String(count)
}
